import SyncIcon from '@mui/icons-material/Sync'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import { Button as MuiButton, CircularProgress } from '@mui/material'
import { FC, useState } from 'react'
import {
  Datagrid, DateField, FilterList, FilterListItem, FunctionField, List, TextField,
  TopToolbar, useNotify, useRefresh, useTranslate,
} from 'react-admin'
import { FilterAside, TierChip } from '../../components'
import { postJson, getUrl } from '../../providers/data'
import { OrganizationOut } from '../../types/apiRequests'
import { getPerPage } from '../../utils/usePerPage'

const SyncButton: FC = () => {
  const notify = useNotify()
  const refresh = useRefresh()
  const translate = useTranslate()
  const [loading, setLoading] = useState(false)

  const handleSync = async () => {
    setLoading(true)
    try {
      const result = await postJson<{
        created: number; updated: number; deleted: number; reactivated: number
      }>(`${getUrl()}/organization/sync`)
      notify(
        translate('data.orgSyncResult', {
          created: result.created,
          updated: result.updated,
          deleted: result.deleted,
          reactivated: result.reactivated,
        }),
        { type: 'success' },
      )
      refresh()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <MuiButton
      size="small"
      startIcon={loading ? <CircularProgress size={16} /> : <SyncIcon />}
      onClick={() => void handleSync()}
      disabled={loading}
      sx={{ textTransform: 'none', fontWeight: 600 }}
    >
      {translate('data.sync')}
    </MuiButton>
  )
}

const ListActions: FC = () => (
  <TopToolbar>
    <SyncButton />
  </TopToolbar>
)

const OrganizationFilters: FC = () => {
  const translate = useTranslate()
  return (
    <FilterAside>
      <FilterList label={translate('filters.status')} icon={<PowerSettingsNewIcon />}>
        <FilterListItem label="filters.active" value={{ deleted: false }} />
        <FilterListItem label="filters.deleted" value={{ deleted: true }} />
      </FilterList>

      <FilterList label={translate('filters.tier')} icon={<WorkspacePremiumIcon />}>
        {/* eslint-disable camelcase */}
        <FilterListItem label="filters.tierFree" value={{ hub_tier: 'free' }} />
        <FilterListItem label="filters.tierTeam" value={{ hub_tier: 'team' }} />
        <FilterListItem label="filters.tierEnterprise" value={{ hub_tier: 'enterprise' }} />
        <FilterListItem label="filters.tierNone" value={{ hub_tier: 'none' }} />
        {/* eslint-enable camelcase */}
      </FilterList>
    </FilterAside>
  )
}

const OrganizationList: FC = () => (
  <List
    exporter={false}
    sort={{ field: 'created', order: 'DESC' }}
    perPage={getPerPage()}
    actions={<ListActions />}
    empty={false}
    aside={<OrganizationFilters />}
  >
    <Datagrid bulkActionButtons={false} rowClick="show">
      <TextField source="display_name" label="data.displayName" />
      <FunctionField<OrganizationOut>
        label="Tier"
        render={(rec) => <TierChip tier={rec.hub_tier} />}
      />
      <DateField source="lastModified" showTime label="data.lastModified" />
      <DateField source="created" showTime label="data.createdAt" />
    </Datagrid>
  </List>
)

export default OrganizationList
